import React, { useEffect, useState } from 'react';
import Modal from '../../shared/components/Modal';
import UserDetailModal from '../../shared/components/UserDetailModal';
import CryptoAmountWithUsd from '../../shared/components/CryptoAmountWithUsd';
import { P2pCashSample, P2pDispute } from '../../shared/types';
import { useUserNames, formatUserLabel } from '../../shared/hooks/useUserNames';
import { getCashImageUrl } from './api';

interface TradeDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: P2pDispute | null;
}

const fmt = (d: string | null | undefined): string =>
  d ? new Date(d).toLocaleString() : '—';

const FeeUnitsRow: React.FC<{
  units: number | null | undefined;
  currency: string;
}> = ({ units, currency }) => {
  if (units === null || units === undefined) return <>—</>;
  return <CryptoAmountWithUsd units={units} currency={currency} />;
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <dt className="text-slate-500">{label}</dt>
    <dd className="text-slate-200">{children}</dd>
  </>
);

// Lazily loads an authenticated banknote scan and opens it in a new tab.
const ScanLink: React.FC<{ tradeId: number; path: string | null; label: string }> = ({ tradeId, path, label }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  if (!path) return <span className="text-slate-600">—</span>;

  const open = async () => {
    if (url) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    setLoading(true);
    try {
      const blobUrl = await getCashImageUrl(tradeId, path);
      setUrl(blobUrl);
      window.open(blobUrl, '_blank', 'noopener');
    } catch {
      setUrl(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button type="button" onClick={open} disabled={loading} className="text-indigo-400 hover:underline text-xs">
      {loading ? 'Loading…' : label}
    </button>
  );
};

const CashVerificationSection: React.FC<{ trade: P2pDispute }> = ({ trade }) => {
  const required = trade.cash_samples_required ?? 0;
  if (trade.mode !== 'otc' || required === 0) return null;
  const samples: P2pCashSample[] = trade.cash_samples ?? [];
  const selected = samples.filter((s) => s.selected_for_check);
  const verified = selected.filter((s) => s.verified_at).length;
  const mismatches = trade.cash_mismatch_count ?? 0;

  return (
    <div className={`card p-4 ${mismatches > 0 ? 'border-amber-500/30' : ''}`}>
      <h4 className="text-sm font-semibold text-slate-300 mb-3">Cash verification</h4>
      <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm mb-3">
        <Row label="Seller banknotes">
          {samples.length} / {required}
          {trade.cash_registered_at ? ` · registered ${fmt(trade.cash_registered_at)}` : ' · in progress'}
        </Row>
        <Row label="Buyer checks">
          {verified} / {trade.cash_checks_required ?? selected.length}
          {trade.cash_verified_at ? ` · verified ${fmt(trade.cash_verified_at)}` : ''}
        </Row>
        <Row label="Mismatched scans">
          {mismatches > 0 ? <span className="text-amber-400">{mismatches}</span> : '0'}
        </Row>
      </dl>
      {samples.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="text-left font-medium pb-1">Serial</th>
              <th className="text-left font-medium pb-1">Scanned</th>
              <th className="text-left font-medium pb-1">Check</th>
              <th className="text-left font-medium pb-1">Images</th>
            </tr>
          </thead>
          <tbody className="text-slate-300">
            {samples.map((s) => (
              <tr key={s.id} className="border-t border-slate-800">
                <td className="py-1 font-mono">{s.serial} <span className="text-slate-500">({s.masked})</span></td>
                <td className="py-1">{fmt(s.scanned_at)}</td>
                <td className="py-1">
                  {!s.selected_for_check
                    ? <span className="text-slate-500">not selected</span>
                    : s.verified_at
                      ? <span className="text-emerald-400">verified {fmt(s.verified_at)}</span>
                      : <span className="text-amber-400">pending</span>}
                </td>
                <td className="py-1 space-x-2">
                  <ScanLink tradeId={trade.id} path={s.image_path} label="seller scan" />
                  <ScanLink tradeId={trade.id} path={s.verify_image_path} label="buyer scan" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card p-4">
    <h4 className="text-sm font-semibold text-slate-300 mb-3">{title}</h4>
    <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">{children}</dl>
  </div>
);

const TradeDetailModal: React.FC<TradeDetailModalProps> = ({ isOpen, onClose, trade }) => {
  const [partyUserId, setPartyUserId] = useState<number | null>(null);
  const [partyTitle, setPartyTitle] = useState<string>('');

  const openParty = (userId: number, label: string) => {
    setPartyUserId(userId);
    setPartyTitle(label);
  };

  const userNames = useUserNames([trade?.buyer_id, trade?.seller_id]);

  if (!trade) return null;

  const buyerLabel = formatUserLabel(trade.buyer_id, userNames);
  const sellerLabel = formatUserLabel(trade.seller_id, userNames);
  const cryptoCurrency = trade.currency;

  const disputeOpenerLabel =
    trade.dispute_opened_by === trade.buyer_id
      ? `Buyer ${buyerLabel}`
      : trade.dispute_opened_by === trade.seller_id
        ? `Seller ${sellerLabel}`
        : trade.dispute_opened_by
          ? `#${trade.dispute_opened_by}`
          : '—';

  const paymentDetails =
    trade.payment_details && Object.keys(trade.payment_details).length > 0
      ? trade.payment_details
      : null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Trade #${trade.id}`} size="2xl">
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => openParty(trade.buyer_id, `Buyer ${buyerLabel}`)}
              className="card p-4 text-left hover:border-indigo-500/40 transition-all"
            >
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Buyer</p>
              <p className="text-base font-semibold text-emerald-400">{buyerLabel}</p>
              <p className="text-xs text-slate-600 font-mono">#{trade.buyer_id}</p>
              <p className="text-xs text-slate-500 mt-1">Click to view profile, sessions and trade history</p>
            </button>
            <button
              type="button"
              onClick={() => openParty(trade.seller_id, `Seller ${sellerLabel}`)}
              className="card p-4 text-left hover:border-indigo-500/40 transition-all"
            >
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Seller</p>
              <p className="text-base font-semibold text-amber-400">{sellerLabel}</p>
              <p className="text-xs text-slate-600 font-mono">#{trade.seller_id}</p>
              <p className="text-xs text-slate-500 mt-1">Click to view profile, sessions and trade history</p>
            </button>
          </div>

          <Section title="Trade details">
            <Row label="Status">
              <span className="badge badge-info">{trade.status}</span>
            </Row>
            <Row label="Amount">
              {trade.amount} {trade.currency}
            </Row>
            <Row label="Price">{trade.price}</Row>
            <Row label="Total">
              {trade.total} {trade.fiat_currency}
            </Row>
            <Row label="Payment method">{trade.payment_method ?? '—'}</Row>
            {trade.mode === 'otc' && (
              <>
                <Row label="Section">OTC (cash in person)</Row>
                <Row label="Meeting city">{trade.offer?.meeting_city ?? '—'}</Row>
                <Row label="Meeting details">{trade.offer?.meeting_details ?? '—'}</Row>
                <Row label="Release code">
                  {trade.release_code_verified_at
                    ? `entered by seller at ${fmt(trade.release_code_verified_at)}`
                    : `not entered (${trade.release_code_attempts ?? 0} failed attempts)`}
                </Row>
              </>
            )}
            <Row label="Offer">
              {trade.offer
                ? `#${trade.offer.id} · ${trade.offer.type}`
                : trade.order_id
                  ? `#${trade.order_id}`
                  : '—'}
            </Row>
            <Row label="Created">{fmt(trade.created_at)}</Row>
            <Row label="Expires at">{fmt(trade.expires_at)}</Row>
            <Row label="Paid at">{fmt(trade.paid_at)}</Row>
            <Row label="Completed at">{fmt(trade.completed_at)}</Row>
            <Row label="Cancelled at">{fmt(trade.cancelled_at)}</Row>
            <Row label="Last updated">{fmt(trade.updated_at)}</Row>
          </Section>

          <CashVerificationSection trade={trade} />

          <Section title="Fees">
            <Row label="Rate (fee schedule)">
              {trade.rate_id ? `#${trade.rate_id}` : '—'}
            </Row>
            <Row label="Fee currency">{trade.fee_currency ?? '—'}</Row>
            <Row label="Seller fee (collected)">
              <FeeUnitsRow units={trade.seller_fee_units} currency={cryptoCurrency} />
            </Row>
            <Row label="Buyer fee (collected)">
              <FeeUnitsRow units={trade.buyer_fee_units} currency={cryptoCurrency} />
            </Row>
            <Row label="Platform fee (total)">
              <FeeUnitsRow units={trade.platform_fee_units} currency={cryptoCurrency} />
            </Row>
          </Section>

          <Section title="Escrow">
            <Row label="Reference">
              <span className="font-mono text-xs break-all">
                {trade.escrow_reference_id ?? '—'}
              </span>
            </Row>
            <Row label="Amount held">
              <FeeUnitsRow
                units={(trade.escrow_amount ?? 0) + (trade.seller_fee_units ?? 0)}
                currency={cryptoCurrency}
              />
            </Row>
            <Row label="Held at">{fmt(trade.escrow_at)}</Row>
            <Row label="Released at">{fmt(trade.escrow_released_at)}</Row>
          </Section>

          {paymentDetails && (
            <Section title="Payment details">
              {Object.entries(paymentDetails).map(([key, value]) => (
                <Row key={key} label={key.replace(/_/g, ' ')}>
                  {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '—')}
                </Row>
              ))}
            </Section>
          )}

          {(trade.buyer_note || trade.seller_note) && (
            <Section title="Notes">
              <Row label="Buyer note">{trade.buyer_note ?? '—'}</Row>
              <Row label="Seller note">{trade.seller_note ?? '—'}</Row>
            </Section>
          )}

          {(trade.dispute || trade.dispute_reason || trade.disputed_at) && (
            <div className="card p-4 border-amber-500/30">
              <h4 className="text-sm font-semibold text-amber-300 mb-3">Dispute</h4>
              <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                {trade.dispute && (
                  <>
                    <Row label="Dispute ID">#{trade.dispute.id}</Row>
                    <Row label="Status">{trade.dispute.status}</Row>
                    <Row label="Priority">{trade.dispute.priority}</Row>
                  </>
                )}
                <Row label="Opened by">{disputeOpenerLabel}</Row>
                <Row label="Opened at">{fmt(trade.disputed_at)}</Row>
                <Row label="Reason">{trade.dispute_reason ?? '—'}</Row>
              </dl>
            </div>
          )}
        </div>
      </Modal>

      <UserDetailModal
        isOpen={partyUserId !== null}
        onClose={() => setPartyUserId(null)}
        userId={partyUserId}
        title={partyTitle}
      />
    </>
  );
};

export default TradeDetailModal;
